import {
  CenterBaseline,
  Icon,
  InlineStack,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
} from '@customerio/pluma-components/react';
import { STATIC_VALUE_SOURCE } from '../../codecs/flat-comparison-filter';
import * as styles from './value-source-select.css';
import { showsValueSource, valueSourceChangePatch } from './value-source';
import { CONDITION_CONTROL_GAP, type ConditionControlSize, type OperatorValuePatch, type ValueOption } from './types';

type ValueSourceSelectProps = {
  /** The row's operator. The select shows only while it can compare. */
  operator: string;
  valueSource: string | undefined;
  /** The surface's sources. Absent means the surface offers no select. */
  options: ValueOption[] | undefined;
  /** The operators the select shows for, where the surface narrows them. Absent means every operator that can compare. */
  comparableOperators?: readonly string[];
  /** The surface's help for the operator, in place of the shared help. Absent means the shared help. */
  help?: string;
  /**
   * Why no source but a fixed value can be picked, shown under the select: a
   * note on a fixed value, an error on any other source, which the surface's
   * backend would refuse. Absent while every source is available.
   */
  unavailableReason?: string;
  disabled: boolean | undefined;
  onChange: (patch: OperatorValuePatch) => void;
  size: ConditionControlSize;
};

/** The help beside the select, unless the surface has its own for the operator. */
const SHARED_HELP =
  "Choose what to compare against. Comparing to the current person's attribute reads the value from their profile when this condition is evaluated, so no value-type setup is needed.";

/**
 * What the condition's value is compared against: the literal typed in the
 * value field, or an attribute read when the condition is evaluated.
 *
 * The help icon is there because people expect to set the value's type here,
 * as they do in segments, and there is nothing to set.
 */
export function ValueSourceSelect({
  operator,
  valueSource,
  options,
  comparableOperators,
  help = SHARED_HELP,
  unavailableReason,
  disabled,
  onChange,
  size,
}: ValueSourceSelectProps) {
  if (options == null || !showsValueSource(operator, options, comparableOperators)) {
    return null;
  }
  const isFixedValue = (valueSource ?? STATIC_VALUE_SOURCE) === STATIC_VALUE_SOURCE;
  const select = (
    <Select
      data-testid="filter-value-source-select"
      ariaLabel="Comparison operator"
      size={size}
      options={options}
      value={valueSource ?? STATIC_VALUE_SOURCE}
      description={isFixedValue ? unavailableReason : undefined}
      error={isFixedValue ? undefined : unavailableReason}
      isDisabled={disabled}
      onChange={(next: string | null) => {
        const patch = valueSourceChangePatch(valueSource, next);
        if (patch != null) {
          onChange(patch);
        }
      }}
    />
  );
  const helpIcon = (
    <CenterBaseline isInline>
      <Popover shouldTriggerOnHover initialFocus={-1}>
        <PopoverTrigger data-testid="filter-value-source-help">
          <Icon name="help" size="sm" color="secondary" />
        </PopoverTrigger>
        <PopoverContent data-testid="filter-value-source-help-content" className={styles.help}>
          {help}
        </PopoverContent>
      </Popover>
    </CenterBaseline>
  );
  if (unavailableReason == null) {
    return (
      <>
        {select}
        {helpIcon}
      </>
    );
  }
  // The reason under the select widens it to the reason's line, which would
  // wrap the help onto the next line, beside the value. Together, the reason
  // wraps instead and the help stays beside the select it explains.
  return (
    <InlineStack data-testid="filter-value-source" gap={CONDITION_CONTROL_GAP[size]} alignBlock="baseline">
      {select}
      {helpIcon}
    </InlineStack>
  );
}
